import React, { useState, useEffect, useMemo } from 'react';
import { Autocomplete, TextField, Checkbox, FormControlLabel, CircularProgress, Box } from '@mui/material';
import { CheckBoxOutlineBlank, CheckBox } from '@mui/icons-material';
import { useGetActiveCustomersForSelectQuery, useGetAllCustomersForSelectQuery } from '../gql/queries.generated';

interface CustomerSelectItem {
    id: number;
    name: string;
}

interface CustomerSelectProps {
  selectedCustomerIds: number[];
  onChange: (selectedIds: number[]) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  includeInactive?: boolean;
  listSelectedInInput?: boolean;
  disabled?: boolean;
  multiple?: boolean;
}

const icon = <CheckBoxOutlineBlank fontSize="small" />;
const checkedIcon = <CheckBox fontSize="small" />;

const CustomerSelect: React.FC<CustomerSelectProps> = ({
  selectedCustomerIds,
  onChange,
  label = 'Select Customers',
  placeholder,
  required = false,
  includeInactive = false,
  listSelectedInInput = false,
  disabled = false,
  multiple = true,
}) => {
  const queryHook = includeInactive ? useGetAllCustomersForSelectQuery : useGetActiveCustomersForSelectQuery;
  const { data, loading, error } = queryHook(); // Error handling can be added if needed

  const [inputValue, setInputValue] = useState('');
  const [selectAllChecked, setSelectAllChecked] = useState(false);

  const allCustomers: CustomerSelectItem[] = data?.customers?.map(c => ({
    id: c.id,
    name: c.name || 'Unnamed Customer'
  })) || [];

  // Filter customers based on input value (matches Autocomplete's filtering)
  const filteredCustomers = useMemo(() => {
    if (!inputValue.trim()) return allCustomers;
    const searchLower = inputValue.toLowerCase();
    return allCustomers.filter(c =>
      c.name.toLowerCase().includes(searchLower) ||
      c.id.toString().includes(searchLower)
    );
  }, [allCustomers, inputValue]);

  const getSelectedCustomers = (): CustomerSelectItem[] => {
    if (!multiple) {
      const customer = allCustomers.find(c => selectedCustomerIds[0] === c.id);
      return customer ? [customer] : [];
    }
    return allCustomers.filter(c => selectedCustomerIds.includes(c.id));
  };

  const getSelectedCustomer = (): CustomerSelectItem | null => {
    if (multiple || selectedCustomerIds.length === 0) return null;
    return allCustomers.find(c => selectedCustomerIds[0] === c.id) || null;
  };

  const handleToggleSelectAll = () => {
    if (!multiple) return;
    if (selectAllChecked) {
      // Uncheck all - clear all selections
      onChange([]);
    } else {
      // Check all - select only the filtered customers
      const filteredIds = filteredCustomers.map(c => c.id);
      // Merge with existing selections that are not in the filtered list
      const existingIds = selectedCustomerIds.filter(id =>
        !filteredCustomers.some(fc => fc.id === id)
      );
      onChange([...existingIds, ...filteredIds]);
    }
  };

  useEffect(() => {
    // Check if all filtered customers are selected
    if (multiple && filteredCustomers.length > 0) {
      const allFilteredSelected = filteredCustomers.every(c =>
        selectedCustomerIds.includes(c.id)
      );
      setSelectAllChecked(allFilteredSelected);
    } else {
      setSelectAllChecked(false);
    }
  }, [selectedCustomerIds, filteredCustomers, multiple]);

  const getPlaceholderText = () => {
    if (loading) return 'Loading Customers...';
    const selectedCount = selectedCustomerIds.length;
    
    if (!multiple) {
      const selected = getSelectedCustomer();
      return selected ? selected.name : (placeholder || label);
    }
    
    if (listSelectedInInput && selectedCount > 0) {
      const names = getSelectedCustomers().map(c => c.name).slice(0, 3).join(', ');
      return selectedCount > 3 ? `${names}, +${selectedCount - 3}` : names;
    }
    if (selectedCount === 1) return '1 Customer Selected';
    if (selectedCount > 1) return `${selectedCount} Customers Selected`;
    return placeholder || label;
  };

  if (error) {
    return <TextField error helperText={`Error loading customers: ${error.message}`} label={label} fullWidth disabled size="small" variant="outlined"/>;
  }

  return (
    <Autocomplete
      multiple={multiple}
      id={`customer-select-${label?.replace(/\s+/g, '-').toLowerCase()}`}
      options={allCustomers}
      value={multiple ? getSelectedCustomers() : getSelectedCustomer()}
      disableCloseOnSelect={multiple}
      disabled={disabled || loading}
      loading={loading}
      getOptionLabel={(option) => `${option.name} (ID: ${option.id})`}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      onChange={(event, newValue) => {
        if (multiple) {
          onChange((newValue as CustomerSelectItem[]).map(c => c.id));
        } else {
          onChange(newValue ? [(newValue as CustomerSelectItem).id] : []);
        }
      }}
      inputValue={inputValue}
      onInputChange={(_, newInputValue, reason) => {
        if (reason === 'input' || reason === 'clear') {
          setInputValue(newInputValue);
        }
      }}
      renderTags={(value) => {
        if (!multiple) return null;
        const selectedCount = value.length;
        if (selectedCount === 0) return null;

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 0.5 }}>
            <span style={{ fontSize: '0.875rem', color: 'inherit' }}>
              {selectedCount === allCustomers.length
                ? `All (${selectedCount})`
                : `${selectedCount} selected`}
            </span>
          </Box>
        );
      }}
      renderOption={(props, option, { selected }) => (
        <Box component="li" {...props} key={option.id}>
          {multiple && (
            <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
          )}
          {option.name} (ID: {option.id})
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label || undefined}
          placeholder={getPlaceholderText()}
          variant="outlined"
          size="small"
          required={required}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <React.Fragment>
                {multiple && (
                  <FormControlLabel
                    sx={{
                      ml: 0,
                      mr: 0,
                      my: 0,
                      minHeight: 0,
                      pointerEvents: 'auto',
                      '& .MuiFormControlLabel-label': {
                        fontSize: '0.75rem',
                        pointerEvents: 'auto',
                      },
                      '& .MuiCheckbox-root': {
                        p: '2px',
                        pointerEvents: 'auto',
                      },
                    }}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleToggleSelectAll(); }}
                    control={<Checkbox icon={icon} checkedIcon={checkedIcon} checked={selectAllChecked} title="Select/Deselect All" size="small" />}
                    label="All"
                  />
                )}
                {params.InputProps.startAdornment}
              </React.Fragment>
            ),
            endAdornment: (
              <React.Fragment>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
    />
  );
};

export default CustomerSelect;